import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { ConfirmationModal } from "../../components/common/ConfirmationModal/ConfirmationModal";
import "./AdminLayout.css";

function AdminLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    logout();
    navigate("/");
  };

  const toggleLang = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    void i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
  };

  return (
    <div
      className={`admin-layout ${collapsed ? "collapsed" : ""} ${mobileOpen ? "mobile-sidebar-open" : ""}`}
    >
      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Navigation (Dark Luxury Theme) */}
      <aside className={`admin-sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="admin-sidebar-header">
          <div className="admin-logo-wrapper">
            <img src="/logoIcon.png" alt="FCS" className="admin-sidebar-logo" />
          </div>
          {!collapsed && (
            <div className="admin-brand-info">
              <span className="admin-sidebar-title">FCS Portal</span>
              <span className="admin-sidebar-tag">
                {t("admin.badge") || "Management"}
              </span>
            </div>
          )}
        </div>

        <div className="admin-sidebar-scroll">
          {/* Navigation Group: Main */}
          <div className="admin-nav-group">
            {!collapsed && (
              <span className="admin-nav-group-title">
                {t("admin.groupMain") || "Overview"}
              </span>
            )}
            <NavLink
              to="/admin"
              end
              className="admin-nav-item"
              title={t("admin.overview")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1.5" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.dashboard")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>
          </div>

          {/* Navigation Group: Management */}
          <div className="admin-nav-group">
            {!collapsed && (
              <span className="admin-nav-group-title">
                {t("admin.groupManage") || "Management"}
              </span>
            )}
            <NavLink
              to="/admin/users"
              className="admin-nav-item"
              title={t("admin.users")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.users")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/bookings"
              className="admin-nav-item"
              title={t("admin.bookings")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.bookings")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/services"
              className="admin-nav-item"
              title={t("admin.services")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.services")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/products"
              className="admin-nav-item"
              title={t("admin.products")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
                  <path d="M3.3 8.3L12 13l8.7-4.7" />
                  <path d="M12 13v9" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.products")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/quotations"
              className="admin-nav-item"
              title={t("admin.quotations")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.quotations")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/consultation-services"
              className="admin-nav-item"
              title={t("admin.consultationServices")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.consultationServices")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/team"
              className="admin-nav-item"
              title={t("admin.team")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.team")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>

            <NavLink
              to="/admin/messages"
              className="admin-nav-item"
              title={t("admin.messages")}
            >
              <div className="admin-nav-icon-box">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              {!collapsed && (
                <span className="admin-nav-text">{t("admin.messages")}</span>
              )}
              <div className="admin-nav-active-pill" />
            </NavLink>
          </div>
        </div>

        <div className="admin-sidebar-footer">
          <NavLink
            to="/"
            className="admin-nav-item admin-nav-back"
            title={t("admin.backToSite")}
          >
            <div className="admin-nav-icon-box">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </div>
            {!collapsed && (
              <span className="admin-nav-text">{t("admin.backToSite")}</span>
            )}
          </NavLink>
        </div>
      </aside>

      {/* Main Container */}
      <div className="admin-main">
        <header className="admin-header">
          <div className="admin-header-left">
            <button
              className="admin-menu-toggle-btn mobile-only"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle Menu"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            {/* Collapse Sidebar Button for desktop */}
            <button
              className="admin-menu-toggle-btn desktop-only"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse Menu"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {collapsed ? (
                  <path d="M11 19l7-7-7-7M3 12h15" />
                ) : (
                  <path d="M13 5l-7 7 7 7M21 12H6" />
                )}
              </svg>
            </button>

            {/* Header Greeting */}
            <div className="admin-header-greeting">
              <h2 className="admin-greeting-text">
                {t("admin.welcomeBack") || "Hello"} {user?.name?.split(" ")[0]}{" "}
                👋
              </h2>
              <span className="admin-date-text">
                {new Date().toLocaleDateString(i18n.resolvedLanguage, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="admin-header-right">
            {/* Language Switcher */}
            <button
              className="admin-header-icon-btn admin-lang-btn"
              onClick={toggleLang}
              title={i18n.language === "ar" ? "English" : "العربية"}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span className="lang-badge">
                {i18n.language === "ar" ? "EN" : "AR"}
              </span>
            </button>

            {/* Profile Section */}
            <div className="admin-profile-section">
              <div className="admin-avatar-wrapper">
                <span className="admin-avatar-initial">
                  {user?.name?.charAt(0).toUpperCase() || "A"}
                </span>
                <span className="admin-avatar-status" />
              </div>
              <div className="admin-profile-meta desktop-only">
                <span className="admin-profile-name">{user?.name}</span>
                <span className="admin-profile-role">
                  {user?.role?.replace("_", " ").toLowerCase()}
                </span>
              </div>
            </div>

            {/* Logout Action */}
            <button
              className="admin-header-icon-btn admin-logout-btn"
              onClick={handleLogoutClick}
              title={t("admin.logout") || "Logout"}
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      <ConfirmationModal
        isOpen={logoutModalOpen}
        title={t('admin.logoutConfirmTitle')}
        message={t('admin.logoutConfirmDesc')}
        confirmLabel={t('admin.logout') || 'Logout'}
        cancelLabel={t('admin.cancel') || 'Cancel'}
        onConfirm={handleConfirmLogout}
        onCancel={() => setLogoutModalOpen(false)}
        type="warning"
      />
    </div>
  );
}

export default AdminLayout;
